import { useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
    searchClientDocuments,
    updateDocumentStatus,
    createCheckRegisterUploadUrl,
    rejectCheckRegister,
} from "../../../utils/apiUtils";
import { useAuthStore } from "../../../store/authStore";

const DEFAULT_FILTERS = {
    status: "in-review",
    accounts: [],
    dateFrom: null,
    dateTo: null,
};

const DEFAULT_PAGE_SIZE = 20;

const useCheckRegisterReview = () => {
    const { email: reviewer } = useAuthStore();

    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [tokenStack, setTokenStack] = useState([]);
    const [currentToken, setCurrentToken] = useState(null);

    const accountHccIds = appliedFilters.accounts?.length
        ? appliedFilters.accounts.map((o) => o.value)
        : undefined;

    const queryFilters = {
        status: appliedFilters.status ?? undefined,
        accountHccIds,
        dateFrom: appliedFilters.dateFrom ?? undefined,
        dateTo: appliedFilters.dateTo ?? undefined,
        limit: pageSize,
        nextToken: currentToken ?? undefined,
    };

    const queryKey = ["checkRegisterDocuments", queryFilters];

    const { data, isLoading, isError, isFetching, refetch } = useQuery({
        queryKey,
        queryFn: () => searchClientDocuments({
            docType: "check-register",
            ...(appliedFilters.status && { status: appliedFilters.status }),
            ...(appliedFilters.dateFrom && { dateFrom: appliedFilters.dateFrom }),
            ...(appliedFilters.dateTo && { dateTo: appliedFilters.dateTo }),
            ...(accountHccIds && { accountHccIds }),
            limit: pageSize,
            ...(currentToken && { nextToken: currentToken }),
        }),
        select: (raw) => ({
            items: raw?.documents ?? [],
            nextToken: raw?.nextToken ?? null,
        }),
        keepPreviousData: true,
    });

    const items = data?.items ?? [];
    const nextToken = data?.nextToken ?? null;

    const applyFilters = useCallback((newFilters) => {
        setAppliedFilters(newFilters);
        setTokenStack([]);
        setCurrentToken(null);
    }, []);

    const clearFilters = useCallback(() => {
        setFilters(DEFAULT_FILTERS);
        applyFilters(DEFAULT_FILTERS);
    }, [applyFilters]);

    const goNext = useCallback(() => {
        if (!nextToken) return;
        setTokenStack((prev) => [...prev, currentToken]);
        setCurrentToken(nextToken);
    }, [nextToken, currentToken]);

    const goPrevious = useCallback(() => {
        if (tokenStack.length === 0) return;
        const stack = [...tokenStack];
        const prev = stack.pop();
        setTokenStack(stack);
        setCurrentToken(prev);
    }, [tokenStack]);

    const onPageSizeChange = useCallback((newSize) => {
        setPageSize(newSize);
        setTokenStack([]);
        setCurrentToken(null);
    }, []);

    const acceptDoc = useMutation({
        mutationFn: ({ sk, accountHccId }) =>
            updateDocumentStatus({ sk, accountHccId, status: "accepted", reviewer }),
        onSuccess: () => {
            window.location.reload();
        },
        onError: () => {
            toast.error("Service unavailable.");
        },
    });

    const createUploadUrl = useMutation({
        mutationFn: ({ accountHccId }) => createCheckRegisterUploadUrl({ accountHccId }),
    });

    const rejectWithFiles = useMutation({
        mutationFn: ({ sk, accountHccId, phiStagingKey, noPhiStagingKey }) =>
            rejectCheckRegister({ sk, accountHccId, reviewer, phiStagingKey, noPhiStagingKey }),
        onSuccess: () => {
            window.location.reload();
        },
        onError: () => {
            toast.error("Service unavailable.");
        },
    });

    return {
        items,
        nextToken,
        isLoading,
        isError,
        isFetching,
        refetch,
        filters,
        setFilters,
        appliedFilters,
        applyFilters,
        clearFilters,
        pageSize,
        onPageSizeChange,
        tokenStack,
        goNext,
        goPrevious,
        mutations: { acceptDoc, createUploadUrl, rejectWithFiles },
    };
};

export default useCheckRegisterReview;
